import { toAlpha2CountryCode } from '@mahawi/eshop-common';
import { type IAddress } from '@mahawi/eshop-common/src/types';
import { useEffect, useState } from 'react';

export enum EGeocodeError {
  NOT_FOUND = 'notFound',
  UNAVAILABLE = 'unavailable',
}

interface IGeocodeState {
  lat: number | null;
  lon: number | null;
  loading: boolean;
  error: EGeocodeError | null;
}

interface IGeocodeResult {
  lat: string;
  lon: string;
}

function joinAddressParts(parts: Array<string | undefined>): string {
  return parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
}

export function getGeocodeCountryCode(address: IAddress): string {
  if (!address.countryTypeIsoCode) {
    return '';
  }

  const countryCode = toAlpha2CountryCode(address.countryTypeIsoCode);

  return countryCode.length === 2 ? countryCode : '';
}

function normalizeCity(city: string | undefined): string {
  return (city ?? '')
    .replace(/\s*\([^)]*\)/g, '')
    .replace(/\s+cedex(\s+\d+)?\s*$/i, '')
    .trim();
}

export function buildGeocodeQueries(address: IAddress): string[] {
  const street = address.street?.trim() ?? '';
  const streetWithNumber = [street, address.streetNumber?.trim()]
    .filter(Boolean)
    .join(' ');
  const [streetFirstSegment] = street.split(',');
  const city = normalizeCity(address.city);
  const zip = address.zip?.trim() ?? '';
  const streetWithZipQueries = zip
    ? [
        joinAddressParts([streetWithNumber, zip]),
        joinAddressParts([streetFirstSegment, zip]),
      ]
    : [];

  const queries = [
    joinAddressParts([streetWithNumber, city, zip]),
    joinAddressParts([street, city, zip]),
    joinAddressParts([streetFirstSegment, city, zip]),
    joinAddressParts([streetFirstSegment, city]),
    ...streetWithZipQueries,
    joinAddressParts([city, zip]),
    joinAddressParts([city]),
    zip,
  ];

  return [...new Set(queries.filter(Boolean))];
}

async function geocodeFirstMatch(
  queries: string[],
  countryCode: string,
): Promise<IGeocodeResult | null> {
  const countryParam = countryCode
    ? `&countrycodes=${encodeURIComponent(countryCode)}`
    : '';

  for (const query of queries) {
    const res = await fetch(
      `/api/map/search?format=json&limit=1&q=${encodeURIComponent(query)}${countryParam}`,
    );

    if (!res.ok) {
      throw new Error(`Geocode request failed with status ${res.status}`);
    }

    const data: IGeocodeResult[] = await res.json();

    if (data.length > 0) {
      return data[0];
    }
  }

  return null;
}

export function useGeocode(address: IAddress): IGeocodeState {
  const [state, setState] = useState<IGeocodeState>({
    lat: null,
    lon: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    geocodeFirstMatch(
      buildGeocodeQueries(address),
      getGeocodeCountryCode(address),
    )
      .then((result) => {
        if (result) {
          setState({
            lat: parseFloat(result.lat),
            lon: parseFloat(result.lon),
            loading: false,
            error: null,
          });
        } else {
          setState({
            lat: null,
            lon: null,
            loading: false,
            error: EGeocodeError.NOT_FOUND,
          });
        }
      })
      .catch(() => {
        setState({
          lat: null,
          lon: null,
          loading: false,
          error: EGeocodeError.UNAVAILABLE,
        });
      });
  }, []);

  return state;
}
